import React, { useState, useEffect, useRef } from 'react';
import { LearningPath } from '../../data/types';
import { GameIcons } from '../styles/GameIcons';
import '../styles/GameStyleUI.css';
import '../screens/students/LearningPathCarousel.css';
import { motion, AnimatePresence } from 'framer-motion';

interface LearningPathCarouselProps {
  paths: LearningPath[];
  student: any; // Using any here to match the parent component's typing
  renderLearningPath: (path: LearningPath, index: number) => React.ReactNode;
  showInHeader?: boolean; // New prop to indicate if path map should be shown in header
}

const LearningPathCarousel: React.FC<LearningPathCarouselProps> = ({ 
  paths, 
  student, 
  renderLearningPath,
  showInHeader = false
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [maxHeight, setMaxHeight] = useState<number | null>(null);
  const carouselRef = useRef<HTMLDivElement>(null);
  const pathRefs = useRef<(HTMLDivElement | null)[]>([]);

  // Calculate how many paths to display based on the number of paths and screen size
  // We'll use a ref to track window width for responsive behavior
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  
  // Update window width on resize
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Determine display count based on window width
  // Show 1 path on small screens (< 768px) and 2 paths on larger screens if available
  const displayCount = paths.length === 1 ? 1 : windowWidth < 768 ? 1 : 2;
  
  // Calculate the maximum height of all learning path blocks
  useEffect(() => {
    if (pathRefs.current.length > 0) {
      const heights = pathRefs.current
        .filter(Boolean)
        .map(el => el?.offsetHeight || 0);
      
      if (heights.length > 0) {
        setMaxHeight(Math.max(...heights));
      }
    }
  }, [paths]);
  
  // Ensure all path refs are initialized
  useEffect(() => {
    // Initialize refs array with the correct length
    pathRefs.current = Array(paths.length).fill(null);
  }, [paths.length]);
  
  // Navigate to the previous path
  const goToPrevious = () => {
    if (isAnimating || paths.length <= displayCount) return;

    setIsAnimating(true);
    setCurrentIndex((prevIndex) => {
      const maxValidIndex = Math.max(0, paths.length - displayCount);
      const newIndex = prevIndex === 0 ? maxValidIndex : prevIndex - 1;
      return Math.max(0, Math.min(newIndex, maxValidIndex));
    });
    setTimeout(() => setIsAnimating(false), 700);
  };

  // Navigate to the next path
  const goToNext = () => {
    if (isAnimating || paths.length <= displayCount) return;

    setIsAnimating(true);
    setCurrentIndex((prevIndex) => {
      // Allow navigation to last path when displaying 2 at a time
      const maxValidIndex = Math.max(0, paths.length - 1);
      if (prevIndex < maxValidIndex) {
        return prevIndex + 1;
      }
      return prevIndex; // clamp at max
    });
    setTimeout(() => setIsAnimating(false), 700);
  };


  // Navigate to a specific path
  const handleNodeClick = (index: number) => {
    if (isAnimating) return;
    setIsAnimating(true);
    // Allow navigation to any path in the collection
    const maxValidIndex = Math.max(0, paths.length - 1);
    setCurrentIndex(Math.min(index, maxValidIndex));
    setTimeout(() => setIsAnimating(false), 700);
  };



  // Determine if we need to show navigation controls
  // Show navigation when there are 2 or more paths, regardless of display count
  const showNavigation = paths.length >= 2;

  // Generate the path map component
  const renderPathMap = () => {
    if (!showNavigation) return null;
    
    return (
      <div className={`path-map-container ${showInHeader ? 'header-path-map' : 'mb-2 px-4 py-2'}`}>
        <div className="flex items-center justify-center">
          <div className="relative w-full max-w-3xl path-map-container">
            {/* Progress Line */}
            {/* Centered Progress Line */}
            <div className="absolute left-0 right-0" style={{ top: 'calc(50% - 7px)', transform: 'translateY(-50%)', height: '8px', background: 'linear-gradient(to right, #fde68a, #facc15, #fde68a)', borderRadius: '9999px', opacity: 0.6 }} />
            
            {/* Node Indicators */}
            <div className="relative flex justify-between path-node-row" style={{height: '3rem'}}>
              {paths.map((path, index) => {
                // Show all nodes for all paths
                if (index >= paths.length) return null;
                
                const isActive = index === currentIndex;
                // Only mark as completed if it's actually completed based on standards
                const totalStandards = path.standards.length;
                const completedStandards = path.standards.filter(standard => {
                  const progress = student?.progress[standard.standardId];
                  return progress && progress.learningPhaseStatus === 'Completed';
                }).length;
                
                const isCompleted = totalStandards > 0 && completedStandards === totalStandards;
                
                // Check if path is locked - a path is locked if all previous paths aren't completed
                const isLocked = index > 0 && !paths.slice(0, index).some(prevPath => {
                  const prevTotalStandards = prevPath.standards.length;
                  const prevCompletedStandards = prevPath.standards.filter(standard => {
                    const progress = student?.progress[standard.standardId];
                    return progress && progress.learningPhaseStatus === 'Completed';
                  }).length;
                  return prevTotalStandards > 0 && prevCompletedStandards === prevTotalStandards;
                });
                
                return (
                  <div key={`node-${index}`} className="path-node-container flex flex-col items-center">
                    <button
                      onClick={() => handleNodeClick(index)}
                      disabled={isAnimating}
                      className={`path-node relative ${isActive ? 'active' : ''} ${isCompleted ? 'completed' : ''} ${isLocked ? 'locked' : ''}`}
                      aria-label={`Go to learning path ${index + 1}: ${path.name}`}
                    >
                      <div className="relative z-10 flex items-center justify-center w-full h-full">
                        {isCompleted ? (
                          <GameIcons.Check size={24} color="#FFFFFF" />
                        ) : isLocked ? (
                          <GameIcons.Lock size={24} color="#FFFFFF" />
                        ) : (
                          <span className="font-bold text-2xl">{index + 1}</span>
                        )}
                        
                        {/* Pulsing effect for active node */}
                        {isActive && (
                          <div className="absolute inset-0 rounded-full animate-pulse opacity-70 bg-yellow-400"></div>
                        )}
                        
                        {/* Glow effect for active and completed nodes */}
                        {(isActive || isCompleted) && (
                          <div className="absolute -inset-1 rounded-full bg-gradient-to-r from-amber-200 to-amber-400 opacity-40 blur-sm -z-10"></div>
                        )}
                      </div>
                    </button>
                    
                    {/* Stars for completed path */}
                    {isCompleted && (
                      <div className="path-stars mt-1 flex justify-center">
                        <GameIcons.Star size={16} color="#FFD700" />
                        <GameIcons.Star size={16} color="#FFD700" />
                        <GameIcons.Star size={16} color="#FFD700" />
                      </div>
                    )}
                    
                    {/* Node number */}
                    <div className="path-number mt-1 text-xs font-bold text-gray-600">
                      {index + 1}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={`learning-path-carousel-container relative ${paths.length === 1 ? 'single-path-container' : ''} ${paths.length === 2 ? 'two-paths-container' : ''}`}>
      {/* Progress Bar / Navigation Nodes - Only show if not in header */}
      {!showInHeader && renderPathMap()}
      
      {/* Carousel Container */}
      <div className="relative overflow-hidden px-8" style={{ 
        marginTop: paths.length === 1 ? '0' : '0px',
      }}>
        {/* Navigation arrows hidden to prevent overlap with standard display */}
        
        {/* Learning Paths Slider - Using Framer Motion for smoother transitions */}
        <div
          ref={carouselRef}
          className="flex relative overflow-hidden"
          style={{ 
            minHeight: paths.length === 1 ? '350px' : '100px'
          }}
        >
          <AnimatePresence initial={false} mode="wait">
            <motion.div
              key={currentIndex}
              className="w-full flex space-x-4"
              initial={{ opacity: 0, x: 50 }}
              animate={{ 
                opacity: 1, 
                x: 0,
                transition: { 
                  type: "spring", 
                  stiffness: 300, 
                  damping: 30,
                  duration: 0.5 
                }
              }}
              exit={{ 
                opacity: 0, 
                x: -50,
                transition: { duration: 0.3 } 
              }}
            >
              {paths.slice(currentIndex, currentIndex + displayCount).map((path, index) => (
                <motion.div 
                  key={path.id} 
                  ref={el => pathRefs.current[currentIndex + index] = el}
                  className={`learning-path-item ${paths.length === 1 ? 'single-path-item' : (paths.length > 1 && index === 0 ? 'active' : '')}`}
                  initial={{ scale: 0.95, opacity: 0 }}
                  animate={{ 
                    scale: 1,
                    opacity: 1,
                    transition: {
                      type: "spring",
                      stiffness: 500,
                      damping: 30,
                      delay: index * 0.05, // Stagger effect
                    }
                  }}
                  style={{ 
                    width: displayCount === 1 ? '100%' : '50%',
                    flexShrink: 0,
                    flexGrow: displayCount === 1 ? 1 : 0,
                    marginBottom: displayCount === 1 && index === 0 && paths.length > 1 ? '1rem' : '0',
                    paddingTop: paths.length === 1 ? '20px' : '8px',
                    paddingBottom: paths.length === 1 ? '20px' : '8px'
                  }}
                >
                  <div className="h-full">
                    {renderLearningPath(path, currentIndex + index)}
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default LearningPathCarousel;
